import React, { useState, useCallback, useRef, useEffect } from 'react';
import { 
  XMarkIcon, 
  PhotoIcon, 
  MagnifyingGlassPlusIcon,
  MagnifyingGlassMinusIcon,
  ArrowsPointingOutIcon
} from '@heroicons/react/24/outline';
import styles from '../../styles/common/imagePreviewModal.module.scss';

const ImagePreviewModal = ({
  open = false,
  onClose,
  imageUrl,
  fileName = 'Unknown Image',
  fileSize = null,
  fileType = 'IMAGE',
  onImageError = null,
}) => {
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [lastMousePosition, setLastMousePosition] = useState({ x: 0, y: 0 });
  const [imageError, setImageError] = useState(false);
  const imageRef = useRef(null);
  const containerRef = useRef(null);
  const modalRef = useRef(null);

  const minZoom = 0.25;
  const maxZoom = 5;
  const zoomStep = 0.25;

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileExtension = (filename) => {
    if (!filename || typeof filename !== 'string') return 'IMAGE';
    const match = filename.trim().match(/\.([a-z0-9]+)$/i);
    if (!match) return null;
    const ext = match[1].toLowerCase();
    const imageExtensions = new Set(['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'svg']);
    return imageExtensions.has(ext) ? ext.toUpperCase() : null;
  };

  const fileExtension = getFileExtension(fileName);
  const isCaptionTitle = !fileExtension;

  useEffect(() => {
    if (open) {
      setScale(1);
      setPosition({ x: 0, y: 0 });
      setImageError(false);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [open, imageUrl]);

  const handleClose = () => {
    if (onClose) {
      onClose();
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === modalRef.current) {
      handleClose();
    }
  };

  const zoomIn = useCallback(() => {
    setScale(prevScale => {
      const newScale = Math.min(prevScale + zoomStep, maxZoom);
      if (newScale !== prevScale) {
        // Scale position proportionally to maintain visual center
        setPosition(prev => {
          const scaleFactor = newScale / prevScale;
          return {
            x: prev.x * scaleFactor,
            y: prev.y * scaleFactor
          };
        });
      }
      return newScale;
    });
  }, []);

  const zoomOut = useCallback(() => {
    setScale(prevScale => {
      const newScale = Math.max(prevScale - zoomStep, minZoom);
      if (newScale !== prevScale) {
        // Scale position proportionally to maintain visual center
        setPosition(prev => {
          const scaleFactor = newScale / prevScale;
          return {
            x: prev.x * scaleFactor,
            y: prev.y * scaleFactor
          };
        });
      }
      return newScale;
    });
  }, []);

  const resetZoom = useCallback(() => {
    setScale(1);
    setPosition({ x: 0, y: 0 });
  }, []);

  const handleWheel = useCallback((e) => {
    if (!imageRef.current || !containerRef.current) return;
    
    e.preventDefault();
    const delta = e.deltaY > 0 ? -zoomStep : zoomStep;
    const newScale = Math.min(Math.max(scale + delta, minZoom), maxZoom);
    
    if (newScale !== scale) {
      // Get mouse position relative to container center
      const containerRect = containerRef.current.getBoundingClientRect();
      const containerCenterX = containerRect.width / 2;
      const containerCenterY = containerRect.height / 2;
      const mouseX = e.clientX - containerRect.left - containerCenterX;
      const mouseY = e.clientY - containerRect.top - containerCenterY;
      
      // Calculate the point under the mouse in image coordinates (before zoom)
      // The image is centered, so we need to account for position offset
      const imageX = (mouseX - position.x) / scale;
      const imageY = (mouseY - position.y) / scale;
      
      // Adjust position to keep the point under the mouse fixed
      setPosition(prev => ({
        x: prev.x - imageX * (newScale - scale),
        y: prev.y - imageY * (newScale - scale)
      }));
      
      setScale(newScale);
    }
  }, [scale, position]);

  const handleMouseDown = useCallback((e) => {
    setIsDragging(true);
    setLastMousePosition({ x: e.clientX, y: e.clientY });
  }, []);

  const handleMouseMove = useCallback((e) => {
    if (!isDragging) return;

    const deltaX = e.clientX - lastMousePosition.x;
    const deltaY = e.clientY - lastMousePosition.y;

    setPosition(prev => ({
      x: prev.x + deltaX,
      y: prev.y + deltaY
    }));

    setLastMousePosition({ x: e.clientX, y: e.clientY });
  }, [isDragging, lastMousePosition, scale]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  const handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      handleClose();
    } else if (event.key === '+' || event.key === '=') {
      zoomIn();
    } else if (event.key === '-') {
      zoomOut();
    } else if (event.key === '0') {
      resetZoom();
    }
  };

  if (!open) return null;

  return (
    <div 
      className={styles.modalBackdrop}
      ref={modalRef}
      onClick={handleBackdropClick}
      onKeyDown={handleKeyDown}
      role="dialog"
      aria-modal="true"
      aria-labelledby="image-preview-title"
    >
      <div className={styles.modalContainer}>
        <div className={styles.modalHeader}>
          <div className={styles.fileInfo}>
            <PhotoIcon className={styles.fileIcon} />
            <div className={styles.fileDetails}>
              <h2
                id="image-preview-title"
                className={isCaptionTitle ? styles.fileNameCaption : styles.fileName}
                title={fileName}
              >
                {fileName}
              </h2>
              <div className={styles.fileMetadata}>
                {fileExtension && (
                  <span className={styles.fileExtensionChip}>
                    {fileExtension}
                  </span>
                )}
                {fileSize && (
                  <span className={styles.fileSize}>
                    {formatFileSize(fileSize)}
                  </span>
                )}
                <span className={styles.zoomLevel}>
                  {Math.round(scale * 100)}%
                </span>
              </div>
            </div>
          </div>

          <div className={styles.controls}>
            <button
              onClick={zoomOut}
              disabled={scale <= minZoom}
              className={`${styles.controlButton} ${scale <= minZoom ? styles.disabled : ''}`}
              aria-label="Zoom Out"
              title="Zoom Out (-)"
            >
              <MagnifyingGlassMinusIcon className={styles.controlIcon} />
            </button>

            <button
              onClick={resetZoom}
              disabled={scale === 1 && position.x === 0 && position.y === 0}
              className={`${styles.controlButton} ${scale === 1 && position.x === 0 && position.y === 0 ? styles.disabled : ''}`}
              aria-label="Reset Zoom"
              title="Reset Zoom (0)"
            >
              <ArrowsPointingOutIcon className={styles.controlIcon} />
            </button>

            <button
              onClick={zoomIn}
              disabled={scale >= maxZoom}
              className={`${styles.controlButton} ${scale >= maxZoom ? styles.disabled : ''}`}
              aria-label="Zoom In"
              title="Zoom In (+)"
            >
              <MagnifyingGlassPlusIcon className={styles.controlIcon} />
            </button>

            <button
              onClick={handleClose}
              className={`${styles.controlButton} ${styles.closeButton}`}
              aria-label="Close"
              title="Close (Esc)"
            >
              <XMarkIcon className={styles.controlIcon} />
            </button>
          </div>
        </div>

        <div
          ref={containerRef}
          className={`${styles.modalContent} ${scale > 1 ? (isDragging ? styles.grabbing : styles.grab) : ''}`}
          onWheel={handleWheel}
        >
          {imageUrl && !imageError ? (
            <img
              ref={imageRef}
              src={imageUrl}
              alt={fileName}
              className={styles.previewImage}
              style={{
                transform: `translate(${position.x}px, ${position.y}px) scale(${scale})`,
                transformOrigin: 'center center',
                transition: isDragging ? 'none' : 'transform 0.2s ease-out',
                maxWidth: '100%',
                // maxHeight: '70vh',
                objectFit: 'contain',
                width: 'auto',
                height: 'auto'
              }}
              onMouseDown={handleMouseDown}
              onError={async () => {
                if (onImageError) {
                  const refreshedUrl = await onImageError();
                  if (refreshedUrl) return;
                }
                setImageError(true);
              }}
              draggable={false}
            />
          ) : (
            <div className={styles.errorFallback}>
              <PhotoIcon className={styles.errorIcon} />
              <p className={styles.errorText}>
                {imageUrl ? 'Failed to load image preview' : 'Image preview not available'}
              </p>
            </div>
          )}

          {scale === 1 && !imageError && imageUrl && (
            <div className={styles.instructionsOverlay}>
              Scroll to zoom • Click and drag when zoomed • Press +/- or 0 to reset
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ImagePreviewModal; 